import React, { useCallback, useEffect, useState } from 'react';
import Axios from 'axios';
import {
  Alert,
  Button,
  Descriptions,
  Modal,
  Popconfirm,
  Select,
  Space,
  Table,
  Tooltip,
  Typography,
  message,
} from 'antd';
import { useSelector } from 'react-redux';
import {
  PageContainer,
  PageHeader,
  StatusBadge,
  EmptyState,
  useResizableColumns,
  ellipsisColumn,
  textSorter,
  dateSorter,
} from '../../common';
import { surveyCode } from '../../Config/surveyCode';

const { Text } = Typography;
const { Option } = Select;

//生成状態（job_status）の表示ラベルと StatusBadge の tone マッピング
//  0=予約中 1=処理中 5=完了 7=一部失敗 9=対象0件 999=エラー
const JOB_STATUS_MAP = {
  0: { label: '予約中', tone: 'warning' },
  1: { label: '処理中', tone: 'info' },
  5: { label: '完了', tone: 'success' },
  7: { label: '一部失敗', tone: 'warning' },
  9: { label: '対象0件', tone: 'neutral' },
  999: { label: 'エラー', tone: 'error' },
};

//曜日の日本語表記（dayjs の ja ロケールは本プロジェクトで未読み込みのため手動で持つ）
const WEEKDAY_JA = ['日', '月', '火', '水', '木', '金', '土'];

//360度系のサーベイコード（回答完了者数と生成対象件数が一致しないため注記を出す）
const RELATION_SURVEY_CODES = ['40010', '60020'];

//E3-Survey 系のサーベイコード（30010 単体 + コース 90301）。
//企業管理者（role 3 / 7）には予約リストを表示しない（Ph.10。
//ダウンロードもサーバー側で role 9 / 2 のみに制限）
const E3_SURVEY_CODES = ['30010', '90301'];

//サーベイ種類のラベル（未登録コードはコードをそのまま表示）
const surveyLabel = (code) => surveyCode[code] || code || '-';

//YYYY/MM/DD HH:mm
const formatDateTime = (dateTime) => {
  if (!dateTime) return '-';
  const date = new Date(dateTime);
  if (isNaN(date.getTime())) return '-';
  return (
    date.getFullYear() +
    '/' +
    ('0' + (date.getMonth() + 1)).slice(-2) +
    '/' +
    ('0' + date.getDate()).slice(-2) +
    ' ' +
    ('0' + date.getHours()).slice(-2) +
    ':' +
    ('0' + date.getMinutes()).slice(-2)
  );
};

//YYYY/MM/DD
const formatDate = (dateTime) => {
  if (!dateTime) return '-';
  const date = new Date(dateTime);
  if (isNaN(date.getTime())) return '-';
  return (
    date.getFullYear() +
    '/' +
    ('0' + (date.getMonth() + 1)).slice(-2) +
    '/' +
    ('0' + date.getDate()).slice(-2)
  );
};

//M月D日(ddd) H:mm — 生成予定日の表示用
//※ 時刻はサーバーが保存した scheduled_date から取り出す（発火時刻を変更しても表示が追従する）
const formatScheduled = (dateTime) => {
  if (!dateTime) return '-';
  const date = new Date(dateTime);
  if (isNaN(date.getTime())) return '-';
  const mm = String(date.getMinutes()).padStart(2, '0');
  return `${date.getMonth() + 1}月${date.getDate()}日(${
    WEEKDAY_JA[date.getDay()]
  }) ${date.getHours()}:${mm}`;
};

//予約できない理由の表示文言（サーバーの ineligible_reason に対応）
//  reserved = 予約中・処理中の予約が既にある
//  no_target = 生成対象が 0 件（回答完了なし、または 360 度系でチーム未完了・役割不足）
//  ※ in_period は Ph.8（D9）で廃止＝回答期間中でも予約できる
const ineligibleText = (season) => {
  if (!season || season.selectable) return '';
  if (season.ineligible_reason === 'reserved') {
    return '　※予約済み';
  }
  if (season.ineligible_reason === 'no_target') {
    return '　※生成対象なし（回答・チーム構成を確認）';
  }
  return '';
};

//回答期間中シーズンの情報注記（Ph.8。予約は可能だが、生成されるのは実行時点までの
//回答完了分である旨をラベル上で示す。ブロック理由ではないため選択可否には影響しない）
const seasonInfoText = (season) => {
  if (!season || season.is_expired) return '';
  return `　（回答期間中〜${formatDate(season.survey_end_date)}）`;
};

//サーベイシーズン選択肢のラベル（予約不可の理由 / 回答期間中の情報注記を淡色で併記）
const renderSeasonLabel = (season) => {
  const note = ineligibleText(season) || seasonInfoText(season);
  return (
    <span>
      {`${season.season_name}（${surveyLabel(season.survey_code)}）`}
      {note && (
        <span
          style={{
            color: 'var(--grey-65)',
            fontSize: 'var(--text-sm)',
          }}
        >
          {note}
        </span>
      )}
    </span>
  );
};

//検索用の文字列（ラベルが JSX のため showSearch 用に別途持つ）
const seasonSearchText = (season) =>
  `${season.season_name}（${surveyLabel(season.survey_code)}）${
    ineligibleText(season) || seasonInfoText(season)
  }`;

/**
 * Content-Disposition ヘッダーからダウンロードファイル名を取り出す。
 * `filename*=UTF-8''<encodeURIComponent 済み>` を優先し、無ければ `filename="..."`、
 * それも無ければ既定名 'report.zip' を返す。
 */
const parseFileName = (disposition) => {
  const fallback = 'report.zip';
  if (!disposition) return fallback;
  //RFC 5987 形式（日本語ファイル名はこちらに入る）
  const extended = disposition.match(/filename\*=UTF-8''([^;]+)/i);
  if (extended && extended[1]) {
    try {
      return decodeURIComponent(extended[1].trim());
    } catch (e) {
      //デコードに失敗した場合は後続の filename= を試す
    }
  }
  const plain = disposition.match(/filename="?([^";]+)"?/i);
  if (plain && plain[1]) return plain[1].trim();
  return fallback;
};

/**
 * arraybuffer で受け取ったエラーレスポンスから error メッセージを取り出す。
 * responseType:'arraybuffer' のときはエラー本文も ArrayBuffer になるため、
 * デコードしてから JSON として解釈する。
 */
const parseArrayBufferError = (error, defaultMessage) => {
  try {
    const data = error && error.response && error.response.data;
    if (data) {
      const text =
        data instanceof ArrayBuffer
          ? new TextDecoder().decode(data)
          : typeof data === 'string'
          ? data
          : null;
      if (text) {
        const parsed = JSON.parse(text);
        if (parsed && parsed.error) return parsed.error;
      }
    }
  } catch (e) {
    //JSON として解釈できない場合は既定メッセージを使う
  }
  return defaultMessage;
};

const PdfBatchJobPage = () => {
  const usr = useSelector((state) => state.user);
  const userData = usr && usr.userData ? usr.userData : null;
  const role = userData ? userData.role : null;
  // 生成予約の「作成」「削除」ができる role
  //   9 = 特権管理者 / 2 = リ・カレント管理者（社内の運用担当）
  //   企業管理者（role 3 / 7）はダウンロードのみ可能
  const canReserve = role === 9 || role === 2;

  const [groups, setGroups] = useState([]);
  const [selGroupCode, setSelGroupCode] = useState('');
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [downloadingId, setDownloadingId] = useState(null);

  //新規生成予約モーダル
  const [reserveOpen, setReserveOpen] = useState(false);
  const [reserveLoading, setReserveLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [seasons, setSeasons] = useState([]);
  const [selSeasonId, setSelSeasonId] = useState('');

  //実行結果詳細モーダル
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailJob, setDetailJob] = useState(null);

  //企業一覧の取得。role 3 / 7 は自社固定（Select は disabled）
  const readGroups = useCallback(async () => {
    try {
      if (!userData) return;
      const res = await Axios.get('/api/group/');
      const list = (res.data && res.data.groups) || [];
      setGroups(list);
      if (role === 3 || role === 7) {
        setSelGroupCode(userData.groupCode);
      }
    } catch (error) {
      console.log(error);
      message.error('企業情報の取得に失敗しました。');
    }
  }, [userData, role]);

  useEffect(() => {
    readGroups();
  }, [readGroups]);

  //予約・結果一覧の取得
  const readJobs = useCallback(async (groupCode) => {
    if (!groupCode) {
      setJobs([]);
      return;
    }
    try {
      setLoading(true);
      const res = await Axios.get(`/api/pdfBatch?group_code=${groupCode}`);
      if (res.data && res.data.success) {
        setJobs(res.data.jobs || []);
      } else {
        throw new Error('予約一覧の取得に失敗しました。');
      }
    } catch (error) {
      console.log(error);
      message.error('予約一覧の取得に失敗しました。');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    readJobs(selGroupCode);
  }, [selGroupCode, readJobs]);

  //role 別の表示絞り込み。
  //  ・企業管理者（role 3 / 7）には E3-Survey（30010 / コース 90301）の予約を表示しない
  //    （Ph.10。ダウンロードも role 9 / 2 のみ＝サーバー側でも 403 で防衛）
  //  ・role 7 はさらに閲覧許可されたサーベイシーズンの予約のみ表示する。
  //    許可シーズンが 0 件の場合はシーズン絞り込みを行わない（role 3 と同等のフォールバック）。
  const visibleJobs = React.useMemo(() => {
    if (!userData) return jobs;
    let list = jobs;
    if (role === 3 || role === 7) {
      list = list.filter(
        (job) => job && !E3_SURVEY_CODES.includes(job.survey_code)
      );
    }
    if (role === 7) {
      const allowed = userData.accessibleSurveySeasons || [];
      if (allowed.length > 0) {
        const allowedSet = new Set(allowed.map((id) => String(id)));
        list = list.filter((job) =>
          allowedSet.has(String(job.survey_season_id))
        );
      }
    }
    return list;
  }, [jobs, userData, role]);

  //同一シーズンに「より新しい実行（処理中/完了/一部失敗）」がある古い予約の _id 集合（Ph.7b）。
  //再予約で S3 が同じキーへ上書きされるため、古い行のダウンロードは
  //「古いファイル一覧 × 最新の内容」という不整合な ZIP になる。行は履歴として残し、
  //ダウンロードだけ無効化する（サーバー側 POST /:id/download にも同じガードあり）。
  //※ 対象0件(9)・エラー(999) の新しい実行は S3 を変更しないため対象外。
  const supersededIds = React.useMemo(() => {
    const set = new Set();
    jobs.forEach((job) => {
      if (![5, 7].includes(job.job_status)) return;
      const newer = jobs.some(
        (other) =>
          String(other.survey_season_id) === String(job.survey_season_id) &&
          [1, 5, 7].includes(other.job_status) &&
          new Date(other.createdAt) > new Date(job.createdAt)
      );
      if (newer) set.add(String(job._id));
    });
    return set;
  }, [jobs]);

  //新規生成予約モーダルを開き、予約可能なサーベイシーズンを取得する
  const openReserveModal = async () => {
    setReserveOpen(true);
    setSelSeasonId('');
    setSeasons([]);
    try {
      setReserveLoading(true);
      const res = await Axios.get(`/api/pdfBatch/seasons/${selGroupCode}`);
      if (res.data && res.data.success) {
        setSeasons(res.data.seasons || []);
      } else {
        throw new Error('サーベイシーズンの取得に失敗しました。');
      }
    } catch (error) {
      console.log(error);
      message.error('サーベイシーズンの取得に失敗しました。');
    } finally {
      setReserveLoading(false);
    }
  };

  //生成予約の登録
  const submitReserve = async () => {
    if (!selSeasonId) {
      message.warning('サーベイシーズンを選択してください。');
      return;
    }
    try {
      setSubmitting(true);
      const res = await Axios.post('/api/pdfBatch', {
        group_code: selGroupCode,
        survey_season_id: selSeasonId,
        executorToken: localStorage.getItem('w_auth'),
      });
      if (res.data && res.data.success) {
        message.success(
          '生成予約を登録しました。翌日 2:00 に生成されます。'
        );
        setReserveOpen(false);
        readJobs(selGroupCode);
      } else {
        throw new Error('予約の登録に失敗しました');
      }
    } catch (error) {
      console.log(error);
      const status = error && error.response && error.response.status;
      const serverError =
        error && error.response && error.response.data
          ? error.response.data.error
          : null;
      if (status === 409) {
        //すでに同じシーズンの予約が存在する
        message.warning(serverError || 'すでに予約されています。');
      } else {
        message.error(serverError || '予約の登録に失敗しました');
      }
    } finally {
      setSubmitting(false);
    }
  };

  //生成予約の削除（role 9 かつ 予約中のみ）
  const deleteJob = async (id) => {
    try {
      const res = await Axios.post(`/api/pdfBatch/${id}/delete`, {
        executorToken: localStorage.getItem('w_auth'),
      });
      if (res.data && res.data.success) {
        message.success('生成予約を削除しました。');
        readJobs(selGroupCode);
      } else {
        throw new Error('予約の削除に失敗しました');
      }
    } catch (error) {
      console.log(error);
      const serverError =
        error && error.response && error.response.data
          ? error.response.data.error
          : null;
      message.error(serverError || '予約の削除に失敗しました');
    }
  };

  //ZIP 一括ダウンロード
  const downloadZip = async (id) => {
    try {
      setDownloadingId(id);
      const res = await Axios.post(
        `/api/pdfBatch/${id}/download`,
        { executorToken: localStorage.getItem('w_auth') },
        { responseType: 'arraybuffer' }
      );
      const fileName = parseFileName(
        res.headers && res.headers['content-disposition']
      );
      const blob = new Blob([res.data], { type: 'application/zip' });
      const fileDownloadUrl = URL.createObjectURL(blob);
      const tempLink = document.createElement('a');
      tempLink.href = fileDownloadUrl;
      tempLink.download = fileName;
      document.body.appendChild(tempLink);
      tempLink.click();
      tempLink.remove();
      //メモリ解放（旧画面では解放漏れがあった）
      URL.revokeObjectURL(fileDownloadUrl);
    } catch (error) {
      console.log(error);
      message.error(
        parseArrayBufferError(error, 'ダウンロードに失敗しました')
      );
    } finally {
      setDownloadingId(null);
    }
  };

  //生成状態バッジ（一部失敗・エラーは原因をツールチップ表示）
  const renderStatus = (job) => {
    const map = JOB_STATUS_MAP[job.job_status] || {
      label: '不明',
      tone: 'neutral',
    };
    const badge = (
      <StatusBadge tone={map.tone} variant="solid">
        {map.label}
      </StatusBadge>
    );
    const reason = job.run_result && job.run_result.last_error;
    if ((job.job_status === 7 || job.job_status === 999) && reason) {
      return (
        <Tooltip title={reason}>
          <span>{badge}</span>
        </Tooltip>
      );
    }
    return badge;
  };

  const rawColumns = [
    {
      title: '予約日時',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 150,
      sorter: dateSorter('createdAt'),
      render: (text) => formatDateTime(text),
    },
    ellipsisColumn({
      title: 'サーベイシーズン名',
      dataIndex: 'season_name',
      key: 'season_name',
      width: 220,
      sorter: textSorter('season_name'),
      render: (text) => text || '-',
    }),
    ellipsisColumn({
      title: 'サーベイ種類',
      dataIndex: 'survey_code',
      key: 'survey_code',
      width: 180,
      render: (text) => surveyLabel(text),
    }),
    {
      title: '生成予定日',
      dataIndex: 'scheduled_date',
      key: 'scheduled_date',
      width: 150,
      //予約中（job_status 0）のみ表示。実行済みは実績を「生成結果」で見る
      render: (text, record) =>
        record.job_status === 0 ? formatScheduled(text) : '-',
    },
    {
      title: '生成状態',
      dataIndex: 'job_status',
      key: 'job_status',
      width: 120,
      align: 'center',
      render: (text, record) => renderStatus(record),
    },
    {
      title: '生成結果',
      dataIndex: 'run_result',
      key: 'run_result',
      width: 200,
      render: (text, record) => {
        if (record.job_status === 0) return '-';
        const r = record.run_result || {};
        const failed = r.failed_count || 0;
        const skipped = r.skipped_count || 0;
        return (
          <div>
            <div>
              {`成功 ${r.generated_count || 0}件 / 失敗 ${failed}件 / スキップ ${skipped}件`}
            </div>
            {(failed > 0 || skipped > 0) && (
              <Button
                type="link"
                size="small"
                style={{ paddingLeft: 0 }}
                onClick={() => {
                  setDetailJob(record);
                  setDetailOpen(true);
                }}
              >
                詳細
              </Button>
            )}
          </div>
        );
      },
    },
    {
      title: '操作',
      key: 'action',
      width: 220,
      resizable: false,
      render: (text, record) => {
        const files = record.generated_files || [];
        const superseded = supersededIds.has(String(record._id));
        const downloadable =
          (record.job_status === 5 || record.job_status === 7) &&
          files.length > 0 &&
          !superseded;
        const downloadButton = (
          <Button
            type="default"
            className="ds-btn-secondary"
            size="small"
            disabled={!downloadable}
            loading={downloadingId === record._id}
            onClick={() => downloadZip(record._id)}
          >
            ダウンロード
          </Button>
        );
        return (
          <Space size="small" wrap>
            {superseded ? (
              <Tooltip title="このシーズンにはより新しい生成結果があります。最新の行からダウンロードしてください。">
                {/* disabled ボタンはイベントを発火しないため span で包んで Tooltip を効かせる */}
                <span>{downloadButton}</span>
              </Tooltip>
            ) : (
              downloadButton
            )}
            {canReserve && record.job_status === 0 && (
              <Popconfirm
                title="この生成予約を削除しますか？"
                okText="削除"
                cancelText="キャンセル"
                onConfirm={() => deleteJob(record._id)}
              >
                <Button danger size="small">
                  予約削除
                </Button>
              </Popconfirm>
            )}
          </Space>
        );
      },
    },
  ];

  const { columns, components, scroll, wrapRef } =
    useResizableColumns(rawColumns);

  //選択中のサーベイシーズン（プレビュー表示用）
  const selSeason = seasons.find((s) => s._id === selSeasonId);

  //実行結果詳細モーダルの中身
  const detailResult = (detailJob && detailJob.run_result) || {};
  const failedItems = detailResult.failed_items || [];
  const skippedItems = detailResult.skipped_items || [];

  return (
    <>
      <PageContainer fluid style={{ paddingTop: 'var(--space-6)' }}>
        <PageHeader
          title="PDF一括生成予約＆ダウンロード"
          subtitle="サーベイシーズンのPDFを、翌日の2:00〜4:00に一括で再生成します。生成後にZIPでまとめてダウンロードできます。"
          extra={
            canReserve && (
              <Button
                type="primary"
                disabled={!selGroupCode}
                onClick={openReserveModal}
              >
                新規生成予約
              </Button>
            )
          }
        />

        <Text
          strong
          style={{ display: 'block', marginBottom: 'var(--space-4)' }}
        >
          企業を選択してください。（必須）
        </Text>
        <div style={{ marginBottom: 'var(--space-5)' }}>
          <Select
            showSearch
            style={{ width: 400 }}
            placeholder="企業選択"
            optionFilterProp="children"
            value={selGroupCode || undefined}
            onChange={(value) => setSelGroupCode(value)}
            //企業管理者（role 3）・シーズン指定管理者（role 7）は自社固定
            disabled={role === 3 || role === 7}
            filterOption={(input, option) =>
              option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
            }
          >
            {groups.map((element) => (
              <Option value={element.group_code} key={element._id}>
                {element.group_name}
              </Option>
            ))}
          </Select>
          <Button
            style={{ marginLeft: 'var(--space-3)' }}
            onClick={() => readJobs(selGroupCode)}
          >
            再読み込み
          </Button>
        </div>

        <div ref={wrapRef}>
          <Table
            columns={columns}
            components={components}
            scroll={scroll}
            dataSource={visibleJobs}
            rowKey="_id"
            size="middle"
            loading={loading}
            pagination={{ pageSize: 50 }}
          />
        </div>
      </PageContainer>

      {/* 新規生成予約モーダル（role 9 のみ） */}
      <Modal
        title="新規生成予約"
        open={reserveOpen}
        onCancel={() => setReserveOpen(false)}
        destroyOnHidden
        //シーズン名＋サーベイ種類＋予約できない理由を1行で読めるよう既定より広くする
        width={760}
        footer={[
          <Button key="cancel" onClick={() => setReserveOpen(false)}>
            キャンセル
          </Button>,
          <Button
            key="submit"
            type="primary"
            loading={submitting}
            disabled={!selSeasonId}
            onClick={submitReserve}
          >
            登録
          </Button>,
        ]}
      >
        {!reserveLoading && seasons.length === 0 ? (
          <EmptyState message="対象のサーベイシーズンがありません。" />
        ) : (
          <>
            <Text
              type="secondary"
              style={{ display: 'block', marginBottom: 'var(--space-3)' }}
            >
              回答期間中のシーズンも予約できます。生成時点（翌日2:00）までの回答完了分が対象になります。最新分が必要な場合は、再度予約してください。
            </Text>
            <Text
              strong
              style={{ display: 'block', marginBottom: 'var(--space-4)' }}
            >
              サーベイシーズンを選択してください。（必須）
            </Text>
            <Select
              showSearch
              style={{ width: '100%' }}
              placeholder="サーベイシーズン選択"
              //label は JSX のため、検索は文字列の searchText を対象にする
              optionFilterProp="searchText"
              filterOption={(input, option) =>
                String((option && option.searchText) || '')
                  .toLowerCase()
                  .indexOf(input.toLowerCase()) >= 0
              }
              loading={reserveLoading}
              value={selSeasonId || undefined}
              onChange={(value) => setSelSeasonId(value)}
              options={seasons.map((s) => ({
                //予約済み・生成対象なしのシーズンは選択不可（理由を併記。回答期間中は情報注記のみで選択可＝Ph.8）
                value: s._id,
                label: renderSeasonLabel(s),
                searchText: seasonSearchText(s),
                disabled: !s.selectable,
              }))}
            />

            {selSeason && (
              <div style={{ marginTop: 'var(--space-5)' }}>
                <Descriptions size="small" column={1}>
                  <Descriptions.Item label="サーベイ種類">
                    {surveyLabel(selSeason.survey_code)}
                  </Descriptions.Item>
                  <Descriptions.Item label="回答期間">
                    {`${formatDate(selSeason.survey_start_date)}〜${formatDate(
                      selSeason.survey_end_date
                    )}`}
                  </Descriptions.Item>
                  <Descriptions.Item label="回答完了者数">
                    {`${selSeason.answered_count || 0} 件`}
                  </Descriptions.Item>
                  {/* 360度系は「回答完了者数」と実際に生成できる件数が一致しない
                      （本人+協力者 全員の回答完了と必須役割が揃ったチームのみ生成対象）ため、
                      夜間バッチと同一判定で算出した件数を併記する */}
                  <Descriptions.Item label="生成対象">
                    {`${selSeason.generatable_count || 0} 件`}
                    {RELATION_SURVEY_CODES.includes(selSeason.survey_code) && (
                      <Text
                        type="secondary"
                        style={{
                          marginLeft: 'var(--space-2)',
                          fontSize: 'var(--text-sm)',
                        }}
                      >
                        （チーム全員の回答が完了し、必須役割が揃ったチームのみ）
                      </Text>
                    )}
                    {/* 回答期間中は実行（翌日2:00）までに回答が増え、実際の生成件数が
                        この予約時点の件数より多くなり得ることを注記する（Ph.8） */}
                    {!selSeason.is_expired && (
                      <Text
                        type="secondary"
                        style={{
                          display: 'block',
                          fontSize: 'var(--text-sm)',
                        }}
                      >
                        ※回答期間中のため、生成時点（翌日2:00）までの回答完了分が対象になります（件数は増える可能性があります）
                      </Text>
                    )}
                  </Descriptions.Item>
                  <Descriptions.Item label="前回の生成">
                    {selSeason.last_job
                      ? `${formatDateTime(selSeason.last_job.updatedAt)}　成功 ${
                          (selSeason.last_job.run_result &&
                            selSeason.last_job.run_result.generated_count) ||
                          0
                        }件`
                      : 'なし'}
                  </Descriptions.Item>
                </Descriptions>

                {/* 対象者が 0 件でも登録は可能（サーバー側で「対象0件」として処理される） */}
                {!selSeason.answered_count && (
                  <Alert
                    type="warning"
                    showIcon
                    style={{ marginTop: 'var(--space-4)' }}
                    message="対象者がいないため生成されません"
                  />
                )}
              </div>
            )}
          </>
        )}
      </Modal>

      {/* 実行結果詳細モーダル */}
      <Modal
        title="実行結果詳細"
        open={detailOpen}
        onCancel={() => setDetailOpen(false)}
        destroyOnHidden
        width={720}
        footer={[
          <Button key="close" onClick={() => setDetailOpen(false)}>
            閉じる
          </Button>,
        ]}
      >
        {failedItems.length > 0 && (
          <div style={{ marginBottom: 'var(--space-6)' }}>
            <Text
              strong
              style={{ display: 'block', marginBottom: 'var(--space-3)' }}
            >
              {`失敗（${failedItems.length}件）`}
            </Text>
            <Table
              size="small"
              pagination={false}
              rowKey={(record, index) => `failed-${index}`}
              dataSource={failedItems}
              columns={[
                { title: '氏名', dataIndex: 'user_name', key: 'user_name' },
                {
                  title: 'サーベイ',
                  dataIndex: 'survey_code',
                  key: 'survey_code',
                  render: (text) => surveyLabel(text),
                },
                { title: '理由', dataIndex: 'reason', key: 'reason' },
              ]}
            />
          </div>
        )}
        {skippedItems.length > 0 && (
          <div>
            <Text
              strong
              style={{ display: 'block', marginBottom: 'var(--space-3)' }}
            >
              {`スキップ（${skippedItems.length}件）`}
            </Text>
            <Table
              size="small"
              pagination={false}
              rowKey={(record, index) => `skipped-${index}`}
              dataSource={skippedItems}
              columns={[
                { title: '氏名', dataIndex: 'user_name', key: 'user_name' },
                { title: '理由', dataIndex: 'reason', key: 'reason' },
              ]}
            />
          </div>
        )}
      </Modal>
    </>
  );
};

export default PdfBatchJobPage;
